<script setup lang="ts">
import { computed, defineProps } from 'vue'
import { storeToRefs } from 'pinia'
import { isEqual } from 'lodash'
import { useImageReviewStore } from '@/store/pages/imageReview/imageReviewStore'
//Icons
import { CheckIcon, TrashCanIcon, RotateLeftIcon } from 'Common/components/elements/icons/Icons'

import { ImageMeasurementType } from '@/modules/types/pages/imageReview/imageMeasurementType'

interface PropTypes {
  locationX: number
  locationY: number
  translationLocation: 'top' | 'bottom'
  removeMeasurement: (measurement: ImageMeasurementType) => void
}
const props = defineProps<PropTypes>()
const store = useImageReviewStore()
const { currentMeasurement, currentHoveredMeasurement, measurements } = storeToRefs(store.measurementsStore)
const { revertCurrentMeasurementToOriginal } = store.measurementsStore
const { handleMeasurementSubmit, handleMeasurementDelete } = store.serverStore
const { currentAnnotation } = storeToRefs(store.annotationsStore)
const originalMeasurement = computed(() => {
  return measurements.value.find((measurement: ImageMeasurementType) => {
    return measurement.measurementUuid === currentMeasurement.value?.measurementUuid
  })
})

const isTempMeasurement = computed<boolean>(() => {
  const isTemp =
    currentMeasurement.value?.measurementUuid !== undefined && currentMeasurement.value.measurementUuid.includes('temp')
  return isTemp
})

const isMeasurementPending = computed<boolean>(() => {
  return currentMeasurement.value?.isPending === true
})

const isMeasurementPendingDeletion = computed<boolean>(() => {
  return currentMeasurement.value?.isDeleting === true
})

const allowConfirm = computed<boolean>(() => {
  const isMeasurementChanged = isEqual(originalMeasurement.value, currentMeasurement.value)
  const isLinkedToAnnotation = originalMeasurement.value.annotationUuid !== null
  const annotationMatch = isLinkedToAnnotation
    ? originalMeasurement.value.annotationUuid === currentAnnotation.value?.annotationUuid
    : currentAnnotation.value?.annotationUuid === undefined

  const hasUnsavedChanges = !isMeasurementChanged || isTempMeasurement.value || !annotationMatch

  const noPendingChanges = isMeasurementPending.value === false && isMeasurementPendingDeletion.value === false

  return hasUnsavedChanges && noPendingChanges
})

const allowReject = computed<boolean>(() => {
  return isMeasurementPending.value === false && isMeasurementPendingDeletion.value === false
})

const shouldRevert = computed<boolean>(() => {
  if (isTempMeasurement.value) {
    return false
  }
  return allowConfirm.value
})

const showMeasurementLabel = computed<boolean>(() => {
  const isMeasurementHoveredDefined =
    currentHoveredMeasurement.value?.length !== undefined && currentHoveredMeasurement.value?.length !== null
  const isMeasurementDefined =
    currentMeasurement.value?.length !== undefined && currentMeasurement.value?.length !== null
  return isMeasurementDefined || isMeasurementHoveredDefined
})

const showOptions = computed<boolean>(() => {
  return currentMeasurement.value !== null && currentMeasurement.value !== undefined
})

const menuOptionsVerticalOffset = computed<string>(() => {
  if (showMeasurementLabel.value === true) {
    return '-275%'
  } else return '-150%'
})

const labelText = computed<string>(() => {
  if (isMeasurementPending.value === true) {
    return 'Calculating Measurement'
  }

  const measurement =
    currentMeasurement.value?.length !== undefined
      ? currentMeasurement.value.length
      : currentHoveredMeasurement.value?.length
  const unit = currentMeasurement.value?.lengthUnits || currentHoveredMeasurement.value?.lengthUnits
  return `${measurement} ${unit}`
})

function onConfirm() {
  handleMeasurementSubmit()
}

function onReject() {
  if (isTempMeasurement.value) {
    props?.removeMeasurement(currentMeasurement.value)
    return
  }
  if (shouldRevert.value === true) revertCurrentMeasurementToOriginal()
  else handleMeasurementDelete()
}
</script>

<template>
  <div
    id="measurement-popover"
    class="absolute z-10"
    :style="{
      top: props.locationY + 'px',
      left: props.locationX + 'px',
    }"
  >
    <template v-if="showMeasurementLabel">
      <div
        class="absolute w-auto whitespace-nowrap top-0 left-0 border border-black bg-black bg-opacity-50 text-white py-1 px-4 shadow-md z-10"
        :style="{
          transform: `translate(-50%, -150%)`,
        }"
      >
        {{ labelText }}
      </div>
    </template>
    <template v-if="showOptions">
      <div
        class="absolute h-8 w-24 top-0 left-0 flex items-center justify-center"
        :style="{
          transform: `translate(-50%, ${menuOptionsVerticalOffset})`,
        }"
      >
        <el-button
          type="success"
          :icon="CheckIcon"
          :disabled="!allowConfirm"
          :loading="isMeasurementPending"
          circle
          size="small"
          @click="onConfirm"
        />
        <el-button
          :type="!shouldRevert ? 'danger' : 'primary'"
          :icon="!shouldRevert ? TrashCanIcon : RotateLeftIcon"
          :disabled="!allowReject"
          :loading="isMeasurementPendingDeletion"
          circle
          size="small"
          @click="onReject"
        />
      </div>
    </template>
  </div>
</template>
